// Composes a subscribed domain event into one sentence whose names are
// plain links, so the reader can open the caregiver, shift, or outreach the
// event is about while the line still reads as a sentence.
// DOMAIN_EVENT_LINE_SPECS is the one allowlist of names the chat draws; a
// composer returns null when the ids its sentence needs are missing or
// malformed, and the caller then falls back to the server's plain summary
// for that same allowlist. isSilentDomainEvent names the one transition
// inside the allowlist that draws nothing at all.
import { CalendarClock, Megaphone, MessageSquare } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { z } from 'zod';

import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { OutreachStartedText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/outreach_start_line';
import { ReferenceLink } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';

export const domainEventObservedPayloadSchema = z
    .object({
        type: z.literal('domain_event_observed').optional(),
        app_event_id: z.uuid(),
        event_name: z.string(),
        entity_type: z.string(),
        entity_id: z.uuid(),
        related_entities: z.array(
            z.object({ type: z.string(), id: z.uuid() }).strip(),
        ),
        occurred_at: z.iso.datetime({ offset: true }),
        summary: z.string().nullable(),
        data: z.record(z.string(), z.unknown()),
        // Resolved at delivery time; absent on events committed before
        // the fields existed. The system user arrives as type "system".
        actor_type: z.string().nullish(),
        actor_user_id: z.uuid().nullish(),
        actor_display_name: z.string().nullish(),
        subscription_id: z.uuid(),
        delivery_mode: z.enum(['active', 'passive']),
    })
    .strip();

export type DomainEventLinePayload = Pick<
    z.infer<typeof domainEventObservedPayloadSchema>,
    | 'event_name'
    | 'entity_type'
    | 'entity_id'
    | 'related_entities'
    | 'data'
    | 'actor_type'
    | 'actor_user_id'
    | 'actor_display_name'
>;

export type DomainEventLine = Readonly<{
    readonly glyph: ReactNode;
    readonly body: ReactNode;
}>;

// The server writes every declared field, so an unset one arrives as null.
const shiftUnassignedDataSchema = z
    .object({ caregiver_name: z.string().nullish() })
    .partial();

const statusChangedDataSchema = z
    .object({ previous_status: z.string(), new_status: z.string() })
    .partial();

const calloutStartedDataSchema = z
    .object({ caregiver_count: z.number().int().nonnegative() })
    .partial();

const caregiverActionDataSchema = z
    .object({ caregiver_id: z.uuid(), caregiver_name: z.string().nullish() })
    .partial();

const caregiverResponseDataSchema = z
    .object({
        response: z.string(),
        caregiver_id: z.uuid().nullish(),
        caregiver_name: z.string().nullish(),
        shift_id: z.uuid().nullish(),
    })
    .partial();

const outreachCancelledDataSchema = z
    .object({ cancelled_by_user_name: z.string().nullish() })
    .partial();

// Mirrors the backend `ShiftGroupChange` dataclass, which serialises every
// field on every change, so the arrays are required rather than defaulted.
// Only the entry count is rendered today; the shape is here so a malformed
// change degrades to the server summary instead of being counted.
const shiftGroupChangeSchema = z
    .object({
        group_id: z.uuid(),
        change: z.enum([
            'created',
            'dissolved',
            'members_changed',
            'mode_changed',
        ]),
        group_mode: z.enum(['all', 'any']).nullable(),
        member_shift_ids: z.array(z.uuid()),
        joined_shift_ids: z.array(z.uuid()),
        left_shift_ids: z.array(z.uuid()),
    })
    .strip();

const shiftsChangedDataSchema = z
    .object({
        added_shift_ids: z.array(z.uuid()),
        removed_shift_ids: z.array(z.uuid()),
        cancelled_shift_ids: z.array(z.uuid()).optional(),
        group_changes: z.array(shiftGroupChangeSchema),
        auto_assign_changed_shift_ids: z.array(z.uuid()),
    })
    .partial();

/** Backend `EntityRef.type` names differ from the reference types'; `contact_attempt`
 * and everything else has no frontend record to open. */
export function toReferenceType(type: string): ReferenceType | null {
    switch (type) {
        case 'outreach':
            return 'callout';
        case 'shift':
        case 'caregiver':
        case 'client':
        case 'callout':
            return type;
        default:
            return null;
    }
}

/** Mirrors the backend's `format_status_label`: `in_progress` → `In Progress`. */
export function formatStatusLabel(value: string): string {
    return value
        .split('_')
        .map(
            (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');
}

type DomainEventLineSpec = Readonly<{
    readonly glyph: ReactNode;
    readonly compose: (
        payload: DomainEventLinePayload,
        formatName: (name: string | null | undefined) => string,
    ) => ReactNode | null;
}>;

const DOMAIN_EVENT_LINE_SPECS: Readonly<Record<string, DomainEventLineSpec>> = {
    'outreach.caregiver_response_recorded': {
        glyph: <Megaphone />,
        compose: composeCaregiverResponse,
    },
    'outreach.cancelled': {
        glyph: <Megaphone />,
        compose: composeOutreachCancelled,
    },
    'outreach.status_changed': {
        glyph: <Megaphone />,
        compose: composeStatusChanged,
    },
    // The count line beside the start narration: the status transition
    // cannot know how many caregivers received contact attempts, so the
    // started event carries it. Zero contacts falls back to the server
    // summary, which states the zero explicitly.
    'outreach.callout.started': {
        glyph: <MessageSquare />,
        compose: composeCalloutStarted,
    },
    'outreach.shifts_changed': {
        glyph: <Megaphone />,
        compose: composeShiftsChanged,
    },
    // `shift.assigned` is deliberately absent: an assignment is already
    // told by the caregiver's acceptance line or the outreach's fill, and
    // beside the approval card that made it, a shift line only repeats it.
    'shift.unassigned': {
        glyph: <CalendarClock />,
        compose: composeShiftUnassigned,
    },
    'shift.caregiver_cancelled': {
        glyph: <CalendarClock />,
        compose: composeCaregiverAction,
    },
    'shift.caregiver_declined': {
        glyph: <CalendarClock />,
        compose: composeCaregiverAction,
    },
    'shift.status_changed': {
        glyph: <CalendarClock />,
        compose: composeStatusChanged,
    },
};

/** The glyph for a supported event name, or null for a name the chat does
 * not draw. */
export function domainEventGlyph(eventName: string): ReactNode | null {
    return DOMAIN_EVENT_LINE_SPECS[eventName]?.glyph ?? null;
}

/** Whether the chat leaves this event out although its name is drawn: a
 * shift's transition into assigned mirrors the assignment itself, which the
 * acceptance line or the outreach's fill already tells. Nothing draws for
 * it, not even the server's summary. */
export function isSilentDomainEvent(payload: DomainEventLinePayload): boolean {
    if (payload.event_name !== 'shift.status_changed') {
        return false;
    }
    const parsed = statusChangedDataSchema.safeParse(payload.data);
    return parsed.success && parsed.data.new_status === 'assigned';
}

function subjectLink(payload: DomainEventLinePayload): ReactNode | null {
    const type = toReferenceType(payload.entity_type);
    if (type === null) {
        return null;
    }
    return <ReferenceLink type={type} id={payload.entity_id} />;
}

function relatedId(
    payload: DomainEventLinePayload,
    type: ReferenceType,
): string | null {
    const match = payload.related_entities.find(
        (reference) => toReferenceType(reference.type) === type,
    );
    return match?.id ?? null;
}

function roleLink(
    payload: DomainEventLinePayload,
    type: ReferenceType,
    explicitId: string | null | undefined,
): ReactNode | null {
    const id = explicitId ?? relatedId(payload, type);
    if (id === null) {
        return null;
    }
    return <ReferenceLink type={type} id={id} />;
}

export function composeDomainEventLine(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): DomainEventLine | null {
    const spec = DOMAIN_EVENT_LINE_SPECS[payload.event_name];
    if (spec === undefined) {
        return null;
    }
    const body = spec.compose(payload, formatName);
    if (body === null) {
        return null;
    }
    return { glyph: spec.glyph, body };
}

function composeShiftUnassigned(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): ReactNode | null {
    const parsed = shiftUnassignedDataSchema.safeParse(payload.data);
    if (!parsed.success) {
        return null;
    }
    const shift = subjectLink(payload);
    if (shift === null) {
        return null;
    }
    return (
        <Fragment>
            Unassigned {formatName(parsed.data.caregiver_name) || 'a caregiver'}{' '}
            from {shift}
        </Fragment>
    );
}

function composeCalloutStarted(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): ReactNode | null {
    const parsed = calloutStartedDataSchema.safeParse(payload.data);
    const count = parsed.success ? parsed.data.caregiver_count : undefined;
    if (count === undefined || count === 0) {
        return null;
    }
    return (
        <Fragment>
            Phoebe texted {count} {count === 1 ? 'caregiver' : 'caregivers'}
        </Fragment>
    );
}

function composeStatusChanged(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): ReactNode | null {
    const parsed = statusChangedDataSchema.safeParse(payload.data);
    if (!parsed.success) {
        return null;
    }
    const subject = subjectLink(payload);
    const previousStatus = parsed.data.previous_status;
    const newStatus = parsed.data.new_status;
    if (
        subject === null ||
        previousStatus === undefined ||
        newStatus === undefined
    ) {
        return null;
    }
    // A start transition narrates as the action it was — the v3 runner
    // hears about starts only through this subscription (no poke since
    // #16536) — so the narration replaces the generic status line rather
    // than duplicating it.
    if (
        payload.event_name === 'outreach.status_changed' &&
        previousStatus === 'pending' &&
        newStatus === 'in_progress'
    ) {
        return (
            <OutreachStartedText
                automatic={payload.actor_type === 'system'}
                userId={payload.actor_user_id ?? null}
                displayName={payload.actor_display_name ?? null}
                subject={subject}
            />
        );
    }
    return (
        <Fragment>
            {subject} changed from {formatStatusLabel(previousStatus)} to{' '}
            {formatStatusLabel(newStatus)}
        </Fragment>
    );
}

function composeCaregiverAction(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): ReactNode | null {
    const parsed = caregiverActionDataSchema.safeParse(payload.data);
    if (!parsed.success) {
        return null;
    }
    const shift = subjectLink(payload);
    if (shift === null) {
        return null;
    }
    const caregiver =
        roleLink(payload, 'caregiver', parsed.data.caregiver_id) ??
        (formatName(parsed.data.caregiver_name) || 'Caregiver');
    const verb =
        payload.event_name === 'shift.caregiver_cancelled'
            ? 'cancelled'
            : 'declined';
    return (
        <Fragment>
            {caregiver} {verb} {shift}
        </Fragment>
    );
}

function composeCaregiverResponse(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): ReactNode | null {
    const parsed = caregiverResponseDataSchema.safeParse(payload.data);
    if (!parsed.success) {
        return null;
    }
    const caregiver =
        roleLink(payload, 'caregiver', parsed.data.caregiver_id) ??
        (formatName(parsed.data.caregiver_name) || 'Caregiver');
    const shift = roleLink(payload, 'shift', parsed.data.shift_id);
    const shiftNode = shift ?? 'a shift';
    switch (parsed.data.response) {
        case 'available_interested':
            return (
                <Fragment>
                    {caregiver} accepted {shiftNode}
                </Fragment>
            );
        case 'unavailable':
            return (
                <Fragment>
                    {caregiver} declined {shiftNode}
                </Fragment>
            );
        case 'available_not_interested':
        case 'not_a_fit':
            return (
                <Fragment>
                    {caregiver} was marked not a fit for {shiftNode}
                </Fragment>
            );
        case 'no_response':
            if (shift === null) {
                return <Fragment>No response from {caregiver}</Fragment>;
            }
            return (
                <Fragment>
                    No response from {caregiver} for {shift}
                </Fragment>
            );
        case 'unknown':
            return (
                <Fragment>
                    {caregiver}’s reply about {shiftNode} needs review
                </Fragment>
            );
        default: {
            const callout = subjectLink(payload);
            if (callout === null) {
                return null;
            }
            return (
                <Fragment>Caregiver response recorded on {callout}</Fragment>
            );
        }
    }
}

function composeOutreachCancelled(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): ReactNode | null {
    const parsed = outreachCancelledDataSchema.safeParse(payload.data);
    if (!parsed.success) {
        return null;
    }
    const callout = subjectLink(payload);
    if (callout === null) {
        return null;
    }
    const cancelledBy = parsed.data.cancelled_by_user_name;
    return (
        <Fragment>
            {callout} cancelled
            {cancelledBy ? ` by ${cancelledBy}` : ''}
        </Fragment>
    );
}

function composeShiftsChanged(
    payload: DomainEventLinePayload,
    formatName: (name: string | null | undefined) => string,
): ReactNode | null {
    const parsed = shiftsChangedDataSchema.safeParse(payload.data);
    if (!parsed.success) {
        return null;
    }
    const callout = subjectLink(payload);
    if (callout === null) {
        return null;
    }
    const added = parsed.data.added_shift_ids?.length ?? 0;
    const removed = parsed.data.removed_shift_ids?.length ?? 0;
    const cancelled = parsed.data.cancelled_shift_ids?.length ?? 0;
    const groups = parsed.data.group_changes?.length ?? 0;
    const flags = parsed.data.auto_assign_changed_shift_ids?.length ?? 0;
    const terms: Array<string> = [];
    if (added > 0) {
        terms.push(`${added} added`);
    }
    if (removed > 0) {
        terms.push(`${removed} removed`);
    }
    if (cancelled > 0) {
        terms.push(`${cancelled} cancelled`);
    }
    if (groups > 0) {
        const noun = groups === 1 ? 'group' : 'groups';
        terms.push(`${groups} ${noun} changed`);
    }
    if (flags > 0) {
        const noun = flags === 1 ? 'booking flag' : 'booking flags';
        terms.push(`${flags} ${noun} changed`);
    }
    if (terms.length === 0) {
        return <Fragment>Shifts changed on {callout}</Fragment>;
    }
    return (
        <Fragment>
            Shifts changed on {callout}: {terms.join(', ')}
        </Fragment>
    );
}
